import { Component, computed, inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Store } from '@ngrx/store';
import { closeScheduleAppointmentDialog } from '../../../appointments/store/actions/appointments.actions';
import { loadPageWithPatientIdParameter } from '../../../core/store/actions/app.actions';
import { selectIsMobileBrowser } from '../../../core/store/selectors/app.selector';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ProgressSpinnerComponent } from '../../../shared/components/progress-spinner/progress-spinner.component';
import { selectPatientId, selectUserId } from '../../../user/store/selectors/user.selectors';
import { NotificationPreference } from '../../models/notification-preference';
import { NotificationTypeId } from '../../models/notification-type-id';
import { NotificationPreferenceRequest } from '../../models/notification-preference-request';
import {
  getNotificationPreferences,
  saveNotificationPreferences,
} from '../../store/actions/notification-preference.actions';
import {
  selectSaveError,
  selectIsLoading,
  selectIsSaving,
  selectNotificationPreferences,
  selectNotificationTypes,
} from '../../store/selectors/notification-preference.selector';
import { NotificationPreferencesFormComponent } from '../notification-preferences-form/notification-preferences-form.component';

@Component({
  selector: 'app-confirm-appointment',
  imports: [
    MatButtonModule,
    MatCheckboxModule,
    MatDividerModule,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    ProgressSpinnerComponent,
    IconComponent,
    ReactiveFormsModule,
    NotificationPreferencesFormComponent,
  ],
  templateUrl: './confirmation.component.html',
  styleUrl: './confirmation.component.scss',
})
export class ConfirmationComponent implements OnInit {
  @Input() confirmationType!: string;
  @Input() showSave: boolean = true;

  store = inject(Store);
  @ViewChild(NotificationPreferencesFormComponent)
  notificationPreferencesFormComponent!: NotificationPreferencesFormComponent;

  readonly confirmationMessages: Record<string, string> = {
    appointment: 'Your appointment request has been submitted!',
    medication: 'Your refill request has been submitted!',
  };

  notificationTypes = this.store.selectSignal(selectNotificationTypes);
  notificationPreferences = this.store.selectSignal(selectNotificationPreferences);
  isLoading = this.store.selectSignal(selectIsLoading);
  isSaving = this.store.selectSignal(selectIsSaving);
  saveError = this.store.selectSignal(selectSaveError);
  userId = this.store.selectSignal(selectUserId);
  patientId = this.store.selectSignal(selectPatientId);
  isMobileBrowser = this.store.selectSignal(selectIsMobileBrowser);

  filteredNotificationTypes = computed(() => {
    const types = this.notificationTypes();
    if (!types) return null;

    if (this.confirmationType === 'medication') {
      return types.filter(type => type.notificationTypeId !== NotificationTypeId.TextMessage);
    }
    return types;
  });

  ngOnInit(): void {
    this.store.dispatch(getNotificationPreferences());
  }

  triggerUpdatePreferences(): void {
    this.notificationPreferencesFormComponent.updateNotificationPreferences();
  }

  updateNotificationPreferences(notificationPreferences: NotificationPreference): void {
    const notificationPreferenceRequest: NotificationPreferenceRequest = {
      emailAddress: notificationPreferences.emailAddress,
      mobilePhoneNumber: notificationPreferences.mobilePhoneNumber,
      notificationTypeIds: notificationPreferences.notificationTypes.map(type => type.notificationTypeId),
    };
    let successRoute = '';
    if (this.confirmationType === 'medication') {
      successRoute = 'medications';
    } else if (this.confirmationType === 'appointment') {
      successRoute = 'appointments';
    }
    this.store.dispatch(saveNotificationPreferences({ notificationPreferenceRequest, successRoute }));
  }

  cancelNotificationPreferences(isMobileBrowser: boolean): void {
    let route = '';
    if (this.confirmationType === 'medication') {
      route = 'medications';
    } else if (this.confirmationType === 'appointment') {
      route = 'appointments';
    }
    const action =
      isMobileBrowser || this.confirmationType === 'medication'
        ? loadPageWithPatientIdParameter({ route, replaceUrl: true })
        : closeScheduleAppointmentDialog();
    this.store.dispatch(action);
  }
}
